import { BoxProps } from "@mui/material";
import { ReactNode } from "react";

import AppLink, { AppLinkProps } from "@/common/components/Link/AppLink";
import { ReactHelper } from "@/common/helpers/react";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import {
  AppPermission,
  BaseEntityDataDto,
  BaseEntityDto,
  BaseEntityFullRefDto,
  BaseEntityRefDto,
} from "@/core/api/generated/v1";

import { BaseEntityInlineInheritableProps } from "../EntityInline/BaseEntityInline";

export interface BaseEntityLinkProps extends Omit<
  AppLinkProps,
  "content" | "icon" | "tooltipTitle" | "tooltipProps" | "withTooltip"
> {
  entity:
    | BaseEntityDto
    | BaseEntityRefDto
    | BaseEntityFullRefDto
    | BaseEntityDataDto
    | null
    | undefined;
  entityId: string | null | undefined;
  isEnabled?: AppLinkProps["isEnabled"];
  permissions: AppPermission[];
  content: ReactNode;
  inlineProps?: Partial<BaseEntityInlineInheritableProps>;
}

/** Props to be inherited by specific implementations. */
export interface BaseEntityLinkInheritableProps {
  isEnabled?: AppLinkProps["isEnabled"];
  isFetch?: boolean;
  isInheritStyles?: AppLinkProps["isInheritStyles"];
  disableLinkStyles?: AppLinkProps["disableLinkStyles"];
  inlineProps?: Partial<BaseEntityInlineInheritableProps>;
  sx?: BoxProps["sx"];
  onClick?: AppLinkProps["onClick"];
}

export function BaseEntityLink({
  entity,
  entityId,
  isEnabled = true,
  permissions,
  content,
  ...otherProps
}: BaseEntityLinkProps) {
  const { hasPermissions } = useAuthorizationInfo();

  const entityIdComputed = entity?.id || entityId || undefined;
  const isEnabledComputed = isEnabled && !!entityIdComputed && hasPermissions(permissions || []);

  if (!entity && !entityId && !entityIdComputed) {
    return null;
  }

  return (
    <AppLink
      isEnabled={isEnabledComputed}
      {...otherProps}
      onClick={(e) => {
        // console.log("AppLink.onClick", e, e.currentTarget, e.target);

        // fix clicks on link descendant portals (modals, popovers) trigger this link click due to React tree nature.
        ReactHelper.ensureDomDescendantClickedOrStopPropagationAndPreventDefault(e);

        otherProps.onClick && otherProps.onClick(e);
      }}
    >
      {content}
    </AppLink>
  );
}
